// dependencies
import React from "react";
import { View } from "react-native";
import { useIntl } from "react-intl";

// components
import {
  IconPhoneO,
  IconHomeO,
  IconMailO,
} from "@cloudspire/legacy-svg-icon-library";
import {
  List,
  ListItem,
} from "@cloudspire/legacy-resources/src/components/List";
import Text from "@cloudspire/legacy-resources/src/components/Text";
import Heading from "@cloudspire/legacy-resources/src/components/Heading";
import Link from "@cloudspire/legacy-resources/src/components/Link2";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";

// helpers
import bookingDelegationGetInformations from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getInformations";
import bookingDelegationGetName from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getName";

type Props = {
  delegatedChannelId: string;
};

function ContactInformation(props: Props) {
  const { delegatedChannelId } = props;

  const intl = useIntl();
  const configuration = useConfiguration();

  const informations = bookingDelegationGetInformations({
    configuration,
    room: {
      mainChannel: {
        channelId: delegatedChannelId,
      },
    } as any,
  });

  const channelTitle = bookingDelegationGetName({
    configuration,
    room: { mainChannel: { channelId: delegatedChannelId } } as any,
  });

  return (
    <>
      {((informations?.booking?.address1 &&
        informations?.booking?.zipcode &&
        informations?.booking?.city) ||
        informations?.booking?.phone ||
        informations?.booking?.schedule) && (
        <View
          style={{
            padding: emToPx(1),
            backgroundColor: "#ffffff",
            borderRadius: emToPx(0.25),
            shadowColor: "#000000",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 1,
            shadowRadius: 12,
          }}
        >
          <Heading level={2} size="xl" withBorder="bottom">
            {intl.formatMessage({
              defaultMessage: "Infos et réservations 7/7j",
            })}
          </Heading>

          <Stack marginTop={1} />

          {informations?.booking?.schedule && (
            <>
              <Heading level={3}>
                {intl.formatMessage({
                  defaultMessage: "Le service de réservation",
                })}
              </Heading>

              <Stack marginTop={0.5} />

              <List gutter={0.5}>
                <ListItem>
                  <List inline={true} gutter={0.5}>
                    <ListItem>
                      <Text
                        style={{
                          fontSize: emToPx(1.5),
                          lineHeight: 0,
                        }}
                      >
                        <IconPhoneO />
                      </Text>
                    </ListItem>

                    <ListItem
                      style={{ justifyContent: "center", flexShrink: 1 }}
                    >
                      <Text style={{ fontSize: emToPx(0.875) }}>
                        {intl.formatMessage({
                          defaultMessage: "Permanence téléphonique :",
                        })}
                      </Text>

                      <Text style={{ fontSize: emToPx(0.875) }}>
                        {informations.booking.schedule}
                      </Text>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Stack marginTop={2} />
            </>
          )}

          {((informations?.booking?.address1 &&
            informations?.booking?.zipcode &&
            informations?.booking?.city) ||
            informations?.booking?.phone) && (
            <>
              <Heading level={3}>{channelTitle}</Heading>

              <Stack marginTop={0.5} />

              <List gutter={0.5}>
                {informations?.booking?.address1 &&
                  informations?.booking?.zipcode &&
                  informations?.booking?.city && (
                    <ListItem>
                      <List inline={true} gutter={0.5}>
                        <ListItem>
                          <Text
                            style={{
                              fontSize: emToPx(1.5),
                              lineHeight: 0,
                            }}
                          >
                            <IconHomeO />
                          </Text>
                        </ListItem>

                        <ListItem
                          style={{ justifyContent: "center", flexShrink: 1 }}
                        >
                          <Text style={{ fontSize: emToPx(0.875) }}>
                            {`${informations.booking.address1}${
                              informations.booking.address2
                                ? ` ${informations.booking.address2}`
                                : ""
                            }, ${informations.booking.zipcode} ${
                              informations.booking.city
                            }`}
                          </Text>
                        </ListItem>
                      </List>
                    </ListItem>
                  )}

                {informations?.booking?.phone && (
                  <ListItem>
                    <List inline={true} gutter={0.5}>
                      <ListItem>
                        <Text
                          style={{
                            fontSize: emToPx(1.5),
                            lineHeight: 0,
                          }}
                        >
                          <IconPhoneO />
                        </Text>
                      </ListItem>

                      <ListItem style={{ justifyContent: "center" }}>
                        <Link
                          data-gtm="contact-booking-phone"
                          to={`tel:${informations.booking.phone}`}
                          external={true}
                          style={{ fontSize: emToPx(0.875) }}
                        >
                          {informations.booking.phone}
                        </Link>
                      </ListItem>
                    </List>
                  </ListItem>
                )}

                {informations?.booking?.email && (
                  <ListItem>
                    <List inline={true} gutter={0.5}>
                      <ListItem>
                        <Text
                          style={{
                            fontSize: emToPx(1.5),
                            lineHeight: 0,
                          }}
                        >
                          <IconMailO />
                        </Text>
                      </ListItem>

                      <ListItem
                        style={{ justifyContent: "center", flexShrink: 1 }}
                      >
                        <Link
                          to={`mailto:${informations.booking.email}`}
                          external={true}
                          data-gtm="contact-booking-email"
                          style={{ fontSize: emToPx(0.875) }}
                        >
                          {informations.booking.email}
                        </Link>
                      </ListItem>
                    </List>
                  </ListItem>
                )}
              </List>
            </>
          )}
        </View>
      )}
    </>
  );
}

export default ContactInformation;
